import { Box, Checkbox, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import StarBorderRounded from '@mui/icons-material/StarBorderRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ScaleRounded from '@mui/icons-material/ScaleRounded';
import EventRounded from '@mui/icons-material/EventRounded';
import type { BulkSelect } from '../../hooks/useBulkSelect';
import type { ViewMode } from '../ui/ViewToggle';
import type { GroceryItemRow } from '../../services/api';
import { CATEGORIES } from './GroceryItemFormDialog';
import NoteStyleCard, { GlassIconTile } from '../ui/NoteStyleCard';

type Props = {
  items: GroceryItemRow[];
  bulk: BulkSelect;
  view: ViewMode;
  onToggleBought: (i: GroceryItemRow) => void;
  onToggleFavourite: (i: GroceryItemRow) => void;
  onEdit: (i: GroceryItemRow) => void;
  onDelete: (i: GroceryItemRow) => void;
};

const metaFor = (name: string) => CATEGORIES.find((c) => c.name === name) || CATEGORIES[CATEGORIES.length - 1];
const GREEN = '#16A34A';

/** Grocery items in the same card style as Notes: category-coloured gradient header + white body. */
const GroceryCards = ({ items, bulk, view, onToggleBought, onToggleFavourite, onEdit, onDelete }: Props) => {
  const grid = view === 'grid';

  return (
    <Box
      sx={grid
        ? { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(3, minmax(0,1fr))' } }
        : { display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      {items.map((i, idx) => {
        const m = metaFor(i.category);
        const Icon = m.icon;
        const bought = i.status === 'bought';
        const selected = bulk.isSelected(i._id);

        return (
          <NoteStyleCard
            key={i._id}
            accent={m.color}
            grid={grid}
            index={idx}
            selected={selected}
            watermark={Icon}
            header={
              <>
                <GlassIconTile size={grid ? 46 : 52}><Icon /></GlassIconTile>
                <Stack alignItems={grid ? 'flex-end' : 'center'} spacing={0.5}>
                  <Chip
                    size="small"
                    icon={bought ? <CheckRounded sx={{ fontSize: 15 }} /> : undefined}
                    label={bought ? 'Bought' : 'To buy'}
                    sx={{
                      bgcolor: 'rgba(255,255,255,.92)',
                      color: bought ? '#15803D' : m.color,
                      fontWeight: 800,
                      '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.95 }}>{i.category}</Typography>
                </Stack>
              </>
            }
          >
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Checkbox size="small" checked={selected} onChange={() => bulk.toggle(i._id)} sx={{ p: 0.25, ml: -0.5 }} />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800, flex: 1, minWidth: 0, lineHeight: 1.3,
                  textDecoration: bought ? 'line-through' : 'none',
                  textDecorationColor: 'rgba(0,0,0,.35)',
                }}
                noWrap
                title={i.name}
              >
                {i.name}
              </Typography>
              <Tooltip title={i.favourite ? 'Remove from favourites' : 'Add to favourites'}>
                <IconButton size="small" onClick={() => onToggleFavourite(i)} sx={{ p: 0.25, color: i.favourite ? '#F59E0B' : 'text.disabled' }}>
                  {i.favourite ? <StarRounded /> : <StarBorderRounded />}
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              {i.quantity && (
                <Chip size="small" icon={<ScaleRounded sx={{ fontSize: 15 }} />} label={i.quantity} variant="outlined" />
              )}
              <Chip
                size="small"
                icon={<EventRounded sx={{ fontSize: 15 }} />}
                label={new Date(i.addedOn).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                variant="outlined"
              />
              {bought && i.boughtOn && (
                <Chip
                  size="small"
                  label={`Bought ${new Date(i.boughtOn).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                  sx={{ bgcolor: `${GREEN}1F`, color: '#15803D', fontWeight: 700 }}
                />
              )}
            </Stack>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box
                component="button"
                type="button"
                onClick={() => onToggleBought(i)}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                  px: 1.5, py: 0.6, borderRadius: '999px', fontWeight: 800, fontSize: 13, fontFamily: 'inherit',
                  border: '2px solid', borderColor: bought ? GREEN : m.color,
                  color: bought ? '#fff' : m.color,
                  bgcolor: bought ? GREEN : 'transparent',
                  transition: 'all .15s ease',
                  '&:hover': { transform: 'translateY(-1px)', bgcolor: bought ? GREEN : `${m.color}14` },
                }}
              >
                <CheckRounded sx={{ fontSize: 17 }} />
                {bought ? 'Bought' : 'Mark bought'}
              </Box>
              <Stack direction="row" spacing={0.25}>
                <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(i)}><EditRounded fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => onDelete(i)}><DeleteRounded fontSize="small" /></IconButton></Tooltip>
              </Stack>
            </Stack>
          </NoteStyleCard>
        );
      })}
    </Box>
  );
};

export default GroceryCards;
